/* assets/rod-buy.js : add-to-cart state model, quantity validation and the "Sepete eklendi"
   confirmation (WP-1, SPEC 5.7). Loaded globally by snippets/rod-head.liquid, so it also works
   on /cart where the cart drawer markup is not rendered.

   It never disables the add-to-cart button itself and never styles Horizon's [data-added];
   the visible state is data-rod-state on the button (absent | loading | success). */
import { Rod } from '@rod/core';
import { StandardEvents, CartErrorEvent } from '@shopify/events';

const doc = document;
const root = doc.documentElement;
const ROOT_SEL = '[data-rod-buy]';
const DESKTOP = window.matchMedia('(min-width: 990px)');
const SAFETY_MS = 15000;
const SUCCESS_MS = 1500;
const DISMISS_MS = 6000;
const RESUME_MIN_MS = 3000;

const MSG = {
  invalid: 'Lütfen geçerli bir adet girin.',
  allInCart: 'Bu üründen stoktaki tüm adetler sepetinizde.',
  offline: 'Bağlantı sorunu nedeniyle ürün eklenemedi. Lütfen tekrar deneyin.',
  soldOut: 'Bu ürün şu anda stokta yok.',
  generic: 'Ürün sepete eklenemedi.',
  successTitle: 'Sepete eklendi',
  errorTitle: 'Ürün sepete eklenemedi',
};
const GENERIC_SERVER_TEXTS = ['cart error', 'add to cart failed', 'network error during add to cart'];

/** @type {WeakMap<Element, {qty:number, vid:string, timer:number, error:object|null}>} */
const pending = new WeakMap();

/* ------------------------------------------------------------------ small helpers */
/* Adet alanı TEK biçimde geliyor: artı/eksi sayacının <input>'u. Kartta adet
   kontrolü hiç çizilmiyor (bkz. rod-buy-form.liquid), orada bu sorgu boş
   dönüyor ve Horizon data-quantity-default'u gönderiyor. */
const qtyInputOf = (el) => el.querySelector('.rod-qty input');
const msgOf = (el) => (el ? el.querySelector('.rod-buy__qty-msg') : null);
const atcOf = (el) => el.querySelector('.rod-buy__atc');
const variantIdOf = (el) => {
  const input = el.querySelector('input[name="id"]');
  return input ? String(input.value || '') : '';
};

function sourceLinkOf(el) {
  const hidden = el.querySelector('input[data-rod-qty-source]');
  if (!hidden) return null;
  const id = hidden.getAttribute('data-rod-qty-source');
  return { hidden, src: id ? doc.getElementById(id) : null };
}

function setMessage(el, text) {
  if (!el) return;
  if (text) {
    el.textContent = text;
    el.hidden = false;
  } else {
    el.textContent = '';
    el.hidden = true;
  }
}

function maxOf(input) {
  const raw = input.getAttribute('max');
  if (raw == null || raw === '') return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}
const cartQtyOf = (input) => parseInt(input.getAttribute('data-cart-quantity') || '0', 10) || 0;

/* Quantity rules (SPEC 14.3 item 2). rod-qty renders min/step from variant.quantity_rule; outside a
   B2B catalog Shopify reports min 1 / increment 1 for every variant, so both read back as 1 and every
   rule below collapses into the v1 behaviour. Horizon's own step check cannot run here: setQuantity
   calls reportValidity(), which a type="text" input (the 5.7.3 contract) ignores silently. */
function intAttr(input, ...names) {
  for (const name of names) {
    const raw = input.getAttribute(name);
    if (raw == null || raw === '') continue;
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 1;
}
const minOf = (input) => intAttr(input, 'data-min', 'min');
const stepOf = (input) => intAttr(input, 'data-step', 'step');
/** Largest value <= n that is min + k*step (k >= 0); never below min. */
function snapDown(n, min, step) {
  if (step <= 1) return Math.max(min, n);
  if (n <= min) return min;
  return min + Math.floor((n - min) / step) * step;
}

function cssPxVar(name) {
  const raw = getComputedStyle(root).getPropertyValue(name).trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

/* ------------------------------------------------------------------ quantity (SPEC 5.7.3) */
/**
 * Validate a raw quantity against the 5.7.3 rules.
 * @returns {{value:number, message:string, blocked:boolean, failed:boolean}}
 */
function validateQty(input, raw) {
  const min = minOf(input);
  const step = stepOf(input);
  const last = parseInt(input.dataset.rodLast || input.value || String(min), 10) || min;
  const parsed = Rod.qty.parse(raw);
  if (!parsed.ok) return { value: last, message: MSG.invalid, blocked: false, failed: true };

  let value = parsed.value;
  // Below the minimum: silent for the v1 minimum of 1 (SPEC 5.7.3 "value < 1 -> 1, no message"),
  // explained when a quantity rule raised it.
  if (value < min) return { value: min, message: min > 1 ? `En az ${min} adet ekleyebilirsiniz.` : '', blocked: false, failed: false };

  let stepMessage = '';
  if (step > 1) {
    const snapped = snapDown(value, min, step);
    if (snapped !== value) {
      value = snapped;
      stepMessage = `Bu ürün ${step} adetlik katlar hâlinde satılır.`;
    }
  }

  const max = maxOf(input);
  if (max != null) {
    const inCart = cartQtyOf(input);
    const remaining = snapDown(max - inCart, min, step);
    if (max - inCart < min) return { value: min, message: MSG.allInCart, blocked: true, failed: true };
    if (value > remaining) {
      const message =
        inCart > 0
          ? `Sepetinizde bu üründen ${inCart} adet var. En fazla ${remaining} adet daha ekleyebilirsiniz.`
          : `En fazla ${remaining} adet ekleyebilirsiniz.`;
      return { value: remaining, message, blocked: false, failed: false };
    }
  }
  return { value, message: stepMessage, blocked: false, failed: false };
}

/** Normalise the input, write the message under its own control and return the result. */
function commitQty(input, messageEl) {
  const owner = input.closest(ROOT_SEL);
  const target = messageEl || msgOf(owner);
  const raw = input.dataset.rodRaw != null ? input.dataset.rodRaw : input.value;
  const res = validateQty(input, raw);
  input.value = String(res.value);
  input.dataset.rodLast = String(res.value);
  delete input.dataset.rodRaw;
  setMessage(target, res.message);
  /* Tapping "Sepete Ekle" blurs the quantity field first, and that blur already normalises "abc"
     back to the last valid value — so by the time the submit guard re-validates, the entry looks
     fine and the add went through silently. The failure is therefore remembered on the input for a
     moment, so the guard that runs immediately afterwards still blocks it (SPEC 5.7.2 rule 2). */
  if (res.failed) input.dataset.rodInvalidAt = String(Date.now());
  else delete input.dataset.rodInvalidAt;
  if (owner) {
    if (res.blocked) owner.setAttribute('data-rod-blocked', '');
    else owner.removeAttribute('data-rod-blocked');
  }
  const selector = input.closest('quantity-selector-component');
  if (selector && typeof selector.updateButtonStates === 'function') selector.updateButtonStates();
  return res;
}

const INVALID_GRACE_MS = 2000;
/** Was this input rejected a moment ago (by the blur the ATC tap itself caused)? Consumes the flag. */
function consumeRecentInvalid(input) {
  if (!input) return false;
  const at = Number(input.dataset.rodInvalidAt || 0);
  delete input.dataset.rodInvalidAt;
  return Boolean(at) && Date.now() - at < INVALID_GRACE_MS;
}

function qtyEventTarget(event) {
  const t = event.target;
  if (!(t instanceof HTMLInputElement)) return null;
  if (!t.matches('.rod-qty input')) return null;
  if (!t.closest(ROOT_SEL)) return null;
  return t;
}

// Window capture runs before Horizon's document-capture declarative dispatcher, so the raw value is
// validated before `setQuantity` silently turns "abc"/"2.5"/"" into 1/2/1.
window.addEventListener(
  'input',
  (event) => {
    const input = qtyEventTarget(event);
    if (input) input.dataset.rodRaw = input.value;
  },
  { capture: true }
);
window.addEventListener(
  'blur',
  (event) => {
    const input = qtyEventTarget(event);
    if (input) commitQty(input);
  },
  { capture: true }
);
window.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const input = qtyEventTarget(event);
    if (!input) return;
    const res = commitQty(input);
    const owner = input.closest(ROOT_SEL);
    // Enter in a quantity field triggers the browser's implicit form submission. When the entry had
    // to be corrected, that submission would run the guard again (which now finds a valid value),
    // clear the message it just wrote and add silently - the customer would never learn why the
    // quantity changed. So a corrected Enter only commits; a second Enter adds (SPEC 5.7.3).
    if (res.failed || res.message || (owner && owner.hasAttribute('data-rod-blocked'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  { capture: true }
);
// Horizon's minus/plus buttons change the value programmatically (no input/blur), so without this
// an invalid entry after stepping would restore the value the page was rendered with instead of
// "the last valid value" (SPEC 5.7.3). The event bubbles from quantity-selector-component.
doc.addEventListener('quantity-selector:update', (event) => {
  const selector = event.target;
  if (!(selector instanceof Element) || !selector.closest(ROOT_SEL)) return;
  const input = selector.querySelector('.rod-qty input');
  if (!input) return;
  const value = Number(event.detail && event.detail.quantity);
  if (!Number.isFinite(value) || value < 1) return;
  input.dataset.rodLast = String(Math.floor(value));
});

/** Re-evaluate the remaining-stock rule of a form after any cart change. */
function reevaluate(el) {
  const input = qtyInputOf(el) || el.querySelector('input[data-rod-qty-source]');
  if (!input) return;
  const messageEl = msgOf(el);
  const max = maxOf(input);
  if (max == null) {
    if (el.hasAttribute('data-rod-blocked')) {
      el.removeAttribute('data-rod-blocked');
      setMessage(messageEl, '');
    }
    return;
  }
  const min = minOf(input);
  const step = stepOf(input);
  const inCart = cartQtyOf(input);
  const left = max - inCart;
  const remaining = snapDown(left, min, step);
  if (el.hasAttribute('data-rod-blocked')) {
    if (left >= min) {
      el.removeAttribute('data-rod-blocked');
      setMessage(messageEl, '');
    }
    return;
  }
  const shown = messageEl && !messageEl.hidden ? messageEl.textContent || '' : '';
  if (!shown.startsWith('Sepetinizde bu üründen')) return;
  if (left < min) {
    el.setAttribute('data-rod-blocked', '');
    setMessage(messageEl, MSG.allInCart);
  } else {
    setMessage(messageEl, `Sepetinizde bu üründen ${inCart} adet var. En fazla ${remaining} adet daha ekleyebilirsiniz.`);
  }
}

/* ------------------------------------------------------------------ button state (SPEC 5.7.2) */
function ensureStateNodes(button) {
  if (button.querySelector('.rod-buy__state')) return;
  const wrap = doc.createElement('span');
  wrap.className = 'rod-buy__state';
  wrap.setAttribute('aria-hidden', 'true');

  const loading = doc.createElement('span');
  loading.className = 'rod-buy__state-loading';
  loading.appendChild(Rod.icon('loader-circle'));

  const success = doc.createElement('span');
  success.className = 'rod-buy__state-success';
  success.appendChild(Rod.icon('check'));
  const added = doc.createElement('span');
  added.textContent = 'Eklendi';
  success.appendChild(added);

  wrap.append(loading, success);

  const sr = doc.createElement('span');
  sr.className = 'rod-sr-only';
  sr.setAttribute('data-rod-state-sr', '');

  button.append(wrap, sr);
}

function setButtonState(el, value) {
  const button = atcOf(el);
  if (!button) return;
  ensureStateNodes(button);
  const sr = button.querySelector('[data-rod-state-sr]');
  if (value) button.setAttribute('data-rod-state', value);
  else button.removeAttribute('data-rod-state');
  // The state overlay is aria-hidden and the visible label is only faded out (never
  // visibility: hidden), so the accessible name stays "Sepete Ekle" and this sr-only span adds the
  // state to it: "Sepete Ekle Ekleniyor" / "Sepete Ekle Eklendi" (WCAG 4.1.2).
  if (value === 'loading') {
    button.setAttribute('aria-busy', 'true');
    if (sr) sr.textContent = 'Ekleniyor';
  } else {
    button.removeAttribute('aria-busy');
    if (sr) sr.textContent = value === 'success' ? 'Eklendi' : '';
  }
}

/* ------------------------------------------------------------------ submit guard (SPEC 5.7.2 rule 2) */
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const el = form.closest(ROOT_SEL);
    if (!el) return;

    const stop = () => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    if (el.hasAttribute('data-rod-busy')) {
      stop();
      return;
    }

    // No stepper and no source input (rail / grid-mode card): Horizon submits data-quantity-default,
    // which is the variant's quantity_rule minimum (1 outside a B2B catalog).
    let quantity = parseInt(el.dataset.rodMin || '1', 10) || 1;
    const own = qtyInputOf(el);
    const link = sourceLinkOf(el);

    if (own) {
      const wasInvalid = consumeRecentInvalid(own);
      const res = commitQty(own);
      if (res.failed || wasInvalid || el.hasAttribute('data-rod-blocked')) {
        stop();
        if (wasInvalid && !res.message) setMessage(msgOf(el), MSG.invalid);
        openConfirm({ state: 'error', message: res.message || MSG.invalid, announced: true, trigger: atcOf(el) });
        return;
      }
      quantity = res.value;
    } else if (link && link.src) {
      const srcOwner = link.src.closest(ROOT_SEL);
      const srcMsg = msgOf(srcOwner) || msgOf(el);
      const wasInvalid = consumeRecentInvalid(link.src);
      const res = commitQty(link.src, srcMsg);
      if (res.failed || wasInvalid || (srcOwner && srcOwner.hasAttribute('data-rod-blocked'))) {
        stop();
        if (wasInvalid && !res.message) setMessage(srcMsg, MSG.invalid);
        /* The offending stepper is usually off screen when the sticky bar was used, so the message
           the customer has to read is scrolled into view (SPEC 5.7.3). */
        try {
          link.src.scrollIntoView({ block: 'center', behavior: 'auto' });
        } catch (e) {
          /* progressive enhancement */
        }
        openConfirm({ state: 'error', message: res.message || MSG.invalid, announced: true, trigger: atcOf(el) });
        return;
      }
      quantity = res.value;
      link.hidden.value = String(quantity);
      el.dataset.quantityDefault = String(quantity);
    }

    el.setAttribute('data-rod-busy', '');
    setButtonState(el, 'loading');
    const timer = setTimeout(() => failAdd(el, { code: 'TIMEOUT' }), SAFETY_MS);
    pending.set(el, { qty: quantity, vid: variantIdOf(el), timer, error: null });
  },
  { capture: true }
);

/* ------------------------------------------------------------------ results */
function pendingRootFor(target) {
  const el = target instanceof Element ? target.closest(ROOT_SEL) : null;
  if (el && pending.has(el)) return el;
  const busy = doc.querySelectorAll(`${ROOT_SEL}[data-rod-busy]`);
  return busy.length === 1 ? busy[0] : null;
}

function clearPending(el) {
  const state = pending.get(el) || null;
  if (state) clearTimeout(state.timer);
  pending.delete(el);
  el.removeAttribute('data-rod-busy');
  return state;
}

function succeedAdd(el, detail, cart) {
  const state = clearPending(el);
  if (!state) return;
  setButtonState(el, 'success');
  setTimeout(() => {
    if (!pending.has(el)) setButtonState(el, null);
  }, SUCCESS_MS);
  setMessage(msgOf(el), '');

  const items = Array.isArray(detail && detail.items) ? detail.items : Rod.cart.state.items || [];
  const line = items.find((i) => String(i.variant_id) === String(state.vid)) || null;
  const count = Number(cart && cart.totalQuantity);
  const total = Number.isFinite(count) ? count : Number(Rod.cart.state.itemCount) || state.qty;
  Rod.announce(`Ürün sepete eklendi. Sepetinizde ${total} ürün var.`);

  openConfirm({
    state: 'success',
    trigger: atcOf(el),
    title: el.getAttribute('data-rod-title') || '',
    image: el.getAttribute('data-rod-image') || '',
    added: state.qty,
    linePrice: line ? Number(line.final_price) * state.qty : null,
    inCart: line ? Number(line.quantity) : null,
  });
}

function failAdd(el, error) {
  const state = clearPending(el);
  setButtonState(el, null);
  const source = error && error.code ? error : (state && state.error) || error || null;
  openConfirm({ state: 'error', message: mapError(source), trigger: atcOf(el) });
}

/** SPEC 5.7.5: single error mapping, reused by rod-cart.js. */
export function mapError(error) {
  const code = error && error.code;
  if (code === 'SERVICE_UNAVAILABLE' || code === 'TIMEOUT' || navigator.onLine === false) return MSG.offline;
  const text = String((error && (error.description || error.message)) || '').trim();
  if (text) {
    if (/you can only add|only \d+|sold out|not enough|is not available|cannot be added|maximum/i.test(text)) {
      const found = text.match(/\d+/);
      if (found) return `Bu üründen stokta yalnızca ${found[0]} adet var.`;
      if (/sold out/i.test(text)) return MSG.soldOut;
      return MSG.generic;
    }
    if (!GENERIC_SERVER_TEXTS.includes(text.toLowerCase())) return text;
  }
  return MSG.generic;
}

if (StandardEvents && StandardEvents.cartLinesUpdate) {
  doc.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const el = pendingRootFor(event.target);
    if (!el) return;
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') {
      succeedAdd(el, null, null);
      return;
    }
    promise.then(
      (result) => {
        const detail = result && result.detail;
        const cart = result && result.cart;
        if (detail && detail.didError) failAdd(el, (pending.get(el) || {}).error || null);
        else succeedAdd(el, detail, cart);
      },
      () => failAdd(el, { code: 'SERVICE_UNAVAILABLE' })
    );
  });
}

doc.addEventListener(CartErrorEvent.eventName, (event) => {
  const el = pendingRootFor(event.target);
  if (!el) return;
  const state = pending.get(el);
  if (!state) return;
  state.error = {
    message: event.error,
    description: event.detail && event.detail.description,
    code: event.code,
  };
});

/* Single writer of data-cart-quantity for every buy form (SPEC 5.7.3, W-11). */
doc.addEventListener('rod:cart:updated', (event) => {
  const items = Array.isArray(event.detail && event.detail.items) ? event.detail.items : [];
  for (const el of doc.querySelectorAll(ROOT_SEL)) {
    const vid = variantIdOf(el);
    if (!vid) continue;
    const line = items.find((i) => String(i.variant_id) === String(vid));
    const inCart = line ? Number(line.quantity) || 0 : 0;
    const selector = el.querySelector('quantity-selector-component');
    if (selector) {
      if (typeof selector.setCartQuantity !== 'function') customElements.upgrade(selector);
      if (typeof selector.setCartQuantity === 'function') selector.setCartQuantity(inCart);
      else {
        const input = qtyInputOf(el);
        if (input) input.setAttribute('data-cart-quantity', String(inCart));
      }
    } else {
      const hidden = el.querySelector('input[data-rod-qty-source]');
      if (hidden) {
        hidden.setAttribute('data-cart-quantity', String(inCart));
        const id = hidden.getAttribute('data-rod-qty-source');
        const src = id ? doc.getElementById(id) : null;
        if (src) src.setAttribute('data-cart-quantity', String(inCart));
      }
    }
    // setCartQuantity may clamp the input down to the new remaining stock without firing an event,
    // so the "last valid value" is refreshed here too (SPEC 5.7.3).
    const own = qtyInputOf(el);
    if (own) {
      const parsed = Rod.qty.parse(own.value);
      if (parsed.ok && parsed.value >= 1) own.dataset.rodLast = String(parsed.value);
    }
    reevaluate(el);
  }
});

/* A back/forward navigation serves cached HTML whose Liquid-rendered data-cart-quantity can be
   stale (the customer changed the cart on another page in between), which would make the
   remaining-stock rule compute against the wrong number and let an over-max quantity through to a
   server 422. @rod/core refreshes on cart events and on a bfcache pageshow, but not on a plain
   back_forward load, so one /cart.js read re-syncs every buy form through rod:cart:updated. */
function syncCartAfterRestore() {
  if (!doc.querySelector(ROOT_SEL)) return;
  let type = '';
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    type = (nav && nav.type) || '';
  } catch (e) {
    type = '';
  }
  if (type !== 'back_forward') return;
  Rod.cart.refresh('back-forward');
}
if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', syncCartAfterRestore, { once: true });
else syncCartAfterRestore();

/* ------------------------------------------------------------------ confirmation (SPEC 5.7.4) */
let cnf = null;
let cnfParts = null;
let cnfTimer = null;
let cnfDeadline = 0;
let cnfRemaining = 0;
let cnfTrigger = null;
let cnfState = null;
let cnfOffEsc = null;
let cnfResize = null;

function buildConfirm() {
  const el = doc.createElement('section');
  el.className = 'rod-cnf';
  el.id = 'rod-cnf';
  el.setAttribute('aria-labelledby', 'rod-cnf-title');

  const head = doc.createElement('div');
  head.className = 'rod-cnf__head';
  const icon = doc.createElement('span');
  icon.className = 'rod-cnf__icon';
  const title = doc.createElement('h2');
  title.className = 'rod-cnf__title';
  title.id = 'rod-cnf-title';
  const close = doc.createElement('button');
  close.type = 'button';
  close.className = 'rod-cnf__close';
  close.setAttribute('aria-label', 'Kapat');
  close.appendChild(Rod.icon('x'));
  close.addEventListener('click', () => closeConfirm({ restoreFocus: true }));
  head.append(icon, title, close);

  const product = doc.createElement('div');
  product.className = 'rod-cnf__product';
  const thumb = doc.createElement('span');
  thumb.className = 'rod-cnf__thumb';
  const info = doc.createElement('div');
  info.className = 'rod-cnf__info';
  const name = doc.createElement('p');
  name.className = 'rod-cnf__name';
  const qty = doc.createElement('p');
  qty.className = 'rod-cnf__qty';
  const totalRow = doc.createElement('div');
  totalRow.className = 'rod-cnf__total';
  const amount = doc.createElement('span');
  amount.className = 'rod-cnf__amount rod-num';
  const tax = doc.createElement('span');
  tax.className = 'rod-cnf__tax';
  totalRow.append(amount, tax);
  const inCart = doc.createElement('p');
  inCart.className = 'rod-cnf__incart';
  info.append(name, qty, totalRow, inCart);
  product.append(thumb, info);

  const message = doc.createElement('p');
  message.className = 'rod-cnf__msg';

  const actions = doc.createElement('div');
  actions.className = 'rod-cnf__actions';
  const go = doc.createElement('a');
  go.className = 'rod-btn rod-btn--primary rod-cnf__go';
  go.href = (Rod.config.routes && Rod.config.routes.cart) || '/cart';
  go.textContent = 'Sepete Git';
  const keep = doc.createElement('button');
  keep.type = 'button';
  keep.className = 'rod-btn rod-btn--outline rod-cnf__keep';
  keep.textContent = 'Alışverişe Devam Et';
  keep.addEventListener('click', () => closeConfirm({ restoreFocus: true }));
  const ok = doc.createElement('button');
  ok.type = 'button';
  ok.className = 'rod-btn rod-btn--outline rod-cnf__ok';
  ok.textContent = 'Tamam';
  ok.addEventListener('click', () => closeConfirm({ restoreFocus: true }));
  actions.append(go, keep, ok);

  el.append(head, product, message, actions);

  el.addEventListener('mouseenter', pauseTimer);
  el.addEventListener('mouseleave', resumeTimer);
  el.addEventListener('focusin', pauseTimer);
  el.addEventListener('focusout', () => setTimeout(resumeTimer, 0));

  cnfParts = { icon, title, product, thumb, name, qty, amount, tax, inCart, message, go, keep, ok };
  return el;
}

function confirmHost() {
  const stack = Rod.overlay.stack;
  for (let i = stack.length - 1; i >= 0; i--) {
    const element = stack[i].element;
    const dialog = element && (element.matches && element.matches('dialog') ? element : element.querySelector && element.querySelector('dialog'));
    if (dialog && dialog.open) return dialog;
  }
  const modals = [...doc.querySelectorAll('dialog')].filter((d) => {
    try {
      return d.matches(':modal');
    } catch (e) {
      return false;
    }
  });
  return modals[modals.length - 1] || doc.body;
}

function pauseTimer() {
  if (!cnfTimer) return;
  clearTimeout(cnfTimer);
  cnfTimer = null;
  cnfRemaining = Math.max(0, cnfDeadline - Date.now());
}

function resumeTimer() {
  if (cnfState !== 'success' || cnfTimer || !cnf || !cnf.classList.contains('is-open')) return;
  if (cnf.matches(':hover') || cnf.contains(doc.activeElement)) return;
  startTimer(Math.max(cnfRemaining, RESUME_MIN_MS));
}

function startTimer(ms) {
  clearTimeout(cnfTimer);
  cnfDeadline = Date.now() + ms;
  cnfRemaining = ms;
  cnfTimer = setTimeout(() => closeConfirm(), ms);
}

/* The confirmation is positioned against the LIVE bottom edge of the header (SPEC 5.7.4 / 3.4
   Rod.headerBottom). `--rod-header-bottom` is written by rod-header.js (WP-2); while that header is
   not live yet the token still holds its pre-JS fallback, which is shorter than the header actually
   on screen, and the popover would be drawn over the category nav row (T-CNF-01). The anchor the CSS
   uses is therefore the larger of the token and the measured header rect; once WP-2 writes the real
   value the token wins by itself and this measurement changes nothing. */
const HEADER_SEL = '#header-group, [data-rod-header], .rod-hdr';

function headerAnchorPx() {
  let bottom = cssPxVar('--rod-header-bottom');
  if (!Number.isFinite(bottom) || bottom < 0) bottom = 0;
  const cap = Math.round(window.innerHeight * 0.45);
  for (const el of doc.querySelectorAll(HEADER_SEL)) {
    const rect = el.getBoundingClientRect();
    if (rect.height === 0 || rect.bottom <= 0) continue;
    const style = getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') continue;
    bottom = Math.max(bottom, Math.min(Math.round(rect.bottom), cap));
  }
  return bottom;
}

function positionConfirm() {
  if (!cnf) return;
  cnf.style.setProperty('--rod-cnf-anchor', `${headerAnchorPx()}px`);
}

let cnfScroll = null;
function watchScroll(on) {
  if (on) {
    if (cnfScroll) return;
    cnfScroll = Rod.throttleRaf(positionConfirm);
    window.addEventListener('scroll', cnfScroll, { passive: true });
  } else if (cnfScroll) {
    window.removeEventListener('scroll', cnfScroll);
    cnfScroll = null;
  }
}

function measureConfirm() {
  positionConfirm();
  if (!cnf || !cnf.classList.contains('is-open') || DESKTOP.matches) {
    root.style.setProperty('--rod-confirm-h', '0px');
    return;
  }
  root.style.setProperty('--rod-confirm-h', `${Math.round(cnf.getBoundingClientRect().height)}px`);
  const above = cssPxVar('--rod-bottom-ui') > 0 || cssPxVar('--rod-consent-offset') > 0;
  if (above) cnf.setAttribute('data-rod-above-bar', '');
  else cnf.removeAttribute('data-rod-above-bar');
}

function openConfirm(options) {
  if (!cnf) cnf = buildConfirm();
  const host = confirmHost();
  if (cnf.parentElement !== host) host.appendChild(cnf);

  const isError = options.state === 'error';
  cnfState = isError ? 'error' : 'success';
  cnf.classList.toggle('rod-cnf--error', isError);
  cnf.classList.toggle('rod-cnf--success', !isError);

  cnfParts.icon.textContent = '';
  cnfParts.icon.appendChild(Rod.icon(isError ? 'circle-alert' : 'circle-check'));
  cnfParts.title.textContent = isError ? MSG.errorTitle : MSG.successTitle;

  cnfParts.product.hidden = isError;
  cnfParts.message.hidden = !isError;
  cnfParts.go.hidden = isError;
  cnfParts.keep.hidden = isError;
  cnfParts.ok.hidden = !isError;

  if (isError) {
    cnfParts.message.textContent = options.message || MSG.generic;
    // A client-side quantity block is already announced by the role="alert" paragraph under the
    // control, so this copy carries no role (SPEC 5.7.5, 3.2 item 12).
    if (options.announced) cnfParts.message.removeAttribute('role');
    else cnfParts.message.setAttribute('role', 'alert');
  } else {
    cnfParts.thumb.textContent = '';
    if (options.image) {
      const img = doc.createElement('img');
      img.src = options.image;
      img.alt = '';
      img.width = 64;
      img.height = 64;
      img.loading = 'lazy';
      cnfParts.thumb.appendChild(img);
      cnfParts.thumb.classList.remove('rod-cnf__thumb--ph');
    } else {
      cnfParts.thumb.appendChild(Rod.icon('package'));
      cnfParts.thumb.classList.add('rod-cnf__thumb--ph');
    }
    cnfParts.name.textContent = options.title || '';
    cnfParts.qty.textContent = `Adet: ${options.added || 1}`;
    if (Number.isFinite(options.linePrice)) {
      cnfParts.amount.textContent = Rod.money(options.linePrice);
      cnfParts.amount.hidden = false;
      cnfParts.tax.textContent = Rod.config.taxLabel || 'KDV Dahil';
      cnfParts.tax.hidden = false;
    } else {
      cnfParts.amount.textContent = '';
      cnfParts.amount.hidden = true;
      cnfParts.tax.textContent = '';
      cnfParts.tax.hidden = true;
    }
    const total = Number(options.inCart);
    if (Number.isFinite(total) && total > (options.added || 1)) {
      cnfParts.inCart.textContent = `Sepetinizde bu üründen toplam ${total} adet var.`;
      cnfParts.inCart.hidden = false;
    } else {
      cnfParts.inCart.textContent = '';
      cnfParts.inCart.hidden = true;
    }
  }

  cnfTrigger = options.trigger || null;
  cnfTabArmed = true;
  cnf.classList.add('is-open');
  doc.body.classList.add('rod-cnf-open');

  clearTimeout(cnfTimer);
  cnfTimer = null;
  if (!isError) startTimer(DISMISS_MS);

  if (!cnfOffEsc) {
    cnfOffEsc = Rod.onEsc((event) => {
      if (!cnf || !cnf.classList.contains('is-open')) return false;
      event.preventDefault();
      closeConfirm({ restoreFocus: true });
      return true;
    });
  }
  if (!cnfResize && 'ResizeObserver' in window) {
    cnfResize = new ResizeObserver(measureConfirm);
    cnfResize.observe(cnf);
  }
  measureConfirm();
  watchScroll(true);
  doc.dispatchEvent(new CustomEvent('rod:cart:confirm', { detail: { state: cnfState } }));
}

function closeConfirm({ restoreFocus = false } = {}) {
  if (!cnf || !cnf.classList.contains('is-open')) return;
  clearTimeout(cnfTimer);
  cnfTimer = null;
  cnf.classList.remove('is-open');
  doc.body.classList.remove('rod-cnf-open');
  watchScroll(false);
  root.style.setProperty('--rod-confirm-h', '0px');
  cnf.removeAttribute('data-rod-above-bar');
  if (cnfOffEsc) {
    cnfOffEsc();
    cnfOffEsc = null;
  }
  const trigger = cnfTrigger;
  cnfTrigger = null;
  cnfState = null;
  if (restoreFocus && trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
}

/* Keyboard reachability (SPEC 5.7.4, T-CNF-02 / T-CNF-04): #rod-cnf is appended to <body>, so in
   focus order it sits after the whole page — from the triggering "Sepete Ekle" it took 65 Tab
   presses to reach it, long after the 6 s auto-dismiss, and "Sepete Git" was unusable by keyboard.
   While the panel is open, Tab from the trigger steps INTO it and the panel hands focus back to the
   trigger; one more Tab then continues past the trigger, so there is no trap. Focus is still never
   moved automatically (the panel opens without taking focus). */
let cnfTabArmed = true;

function cnfFocusables() {
  if (!cnf) return [];
  return [...cnf.querySelectorAll('button, a[href]')].filter((el) => !el.hidden && el.getClientRects().length);
}

doc.addEventListener('keydown', (event) => {
  if (event.key !== 'Tab' || event.defaultPrevented) return;
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  if (!cnf || !cnf.classList.contains('is-open')) return;
  const trigger = cnfTrigger;
  if (!trigger || !trigger.isConnected) return;
  const active = doc.activeElement;
  if (!(active instanceof HTMLElement)) return;
  const items = cnfFocusables();
  if (!items.length) return;

  if (!cnf.contains(active)) {
    if (event.shiftKey || !cnfTabArmed || active !== trigger) return;
    event.preventDefault();
    items[0].focus();
    return;
  }

  const index = items.indexOf(active);
  if (index === -1) return;
  const leavingBack = event.shiftKey && index === 0;
  const leavingForward = !event.shiftKey && index === items.length - 1;
  if (!leavingBack && !leavingForward) return;
  event.preventDefault();
  cnfTabArmed = false;
  trigger.focus({ preventScroll: true });
});

// Any overlay (cart drawer, search modal, menu or filter drawer) closes the confirmation.
doc.addEventListener('rod:overlay:opened', () => closeConfirm());

// Focus must never be obscured (SPEC 3.2 item 14, T-GEN-16).
doc.addEventListener('focusin', (event) => {
  if (!cnf || !cnf.classList.contains('is-open') || !DESKTOP.matches) return;
  const target = event.target;
  if (!(target instanceof HTMLElement) || cnf.contains(target)) return;
  const a = cnf.getBoundingClientRect();
  const b = target.getBoundingClientRect();
  if (b.width === 0 && b.height === 0) return;
  const overlaps = b.left < a.right && b.right > a.left && b.top < a.bottom && b.bottom > a.top;
  if (!overlaps) return;
  if (cnfState === 'success') closeConfirm();
  else target.scrollIntoView({ block: 'center' });
});

DESKTOP.addEventListener('change', measureConfirm);
// A bottom bar appearing/disappearing moves the sheet without resizing it, so the ResizeObserver
// alone would leave the safe-area padding stale.
doc.addEventListener('rod:bottom-ui', measureConfirm);
window.addEventListener('resize', Rod.debounce(measureConfirm, 150));
window.addEventListener('pageshow', (event) => {
  if (event.persisted) closeConfirm();
});

/* Cross-package access without a second module instance: importing this file by a relative URL from
   another asset would resolve to a different URL (no ?v= hash) and register the window listeners twice.
   WP-6's rod-cart.js therefore reuses the error mapper through window.RodBuy (SPEC 5.7.5). */
window.RodBuy = { mapError, openConfirm, closeConfirm };

export { openConfirm, closeConfirm };
